import { containerMemoryPercent } from '@/utils/serviceMonitorAccess';

const BASE_URL = (import.meta.env.VITE_APP_BASE_API as string | undefined) || '/api';

/** Memory warning threshold: strictly greater than this percent. */
export const CONTAINER_MEMORY_WARN_THRESHOLD = 75;

/**
 * Monitor target service (same cards as Service Monitor).
 */
export interface ServiceConfig {
    /** Display name in UI and warning copy. */
    name: string;
    /** Gateway path key, e.g. gateway. */
    key: string;
    /** Card accent color. */
    color: string;
}

export const MONITOR_SERVICES: ServiceConfig[] = [
    { name: 'Gateway', key: 'gateway', color: '#8b5cf6' },
    { name: 'Account', key: 'account', color: '#3b82f6' },
    { name: 'Admin', key: 'admin', color: '#10b981' },
    { name: 'Audit', key: 'audit', color: '#f59e0b' },
    { name: 'Auth', key: 'auth', color: '#ef4444' },
];

/**
 * /actuator/container body.
 */
export interface ContainerStats {
    available: boolean;
    memoryUsed?: number;
    memoryLimit?: number | null;
    cpuPercent?: number | null;
}

/**
 * One service over the container-memory threshold.
 */
export interface ContainerMemoryWarning {
    /** Service display name. */
    serviceName: string;
    /** Rounded percent 0-100. */
    percent: number;
}

/**
 * GET JSON from an actuator URL with the admin token. Non-Result payloads.
 *
 * @param url full actuator url
 * @param timeout abort ms
 * @param allowNon2xx parse body even when HTTP is not 2xx (health)
 * @returns parsed json
 */
export async function fetchActuatorJson(
    url: string,
    timeout = 2000,
    allowNon2xx = false,
): Promise<unknown> {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeout);
    const token = localStorage.getItem('adminToken') || localStorage.getItem('Admin-Token') || '';
    try {
        const res = await fetch(url, {
            signal: ctrl.signal,
            credentials: 'include',
            headers: {
                ...(token ? { 'Admin-Token': token, Authorization: `Bearer ${token}` } : {}),
            },
        });
        if (!res.ok && !allowNon2xx) throw new Error(`HTTP ${res.status}`);
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('json') && !res.ok) {
            throw new Error(`HTTP ${res.status}`);
        }
        return await res.json();
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Load cgroup container stats for one service.
 *
 * @param serviceKey gateway / account / ...
 * @returns stats or null on failure
 */
export async function fetchContainerStats(serviceKey: string): Promise<ContainerStats | null> {
    try {
        const data = await fetchActuatorJson(`${BASE_URL}/${serviceKey}/actuator/container`) as ContainerStats;
        return data;
    } catch {
        return null;
    }
}

/**
 * Collect English warning lines for services whose container memory is above 75%.
 *
 * @returns warnings; empty when none exceed the threshold
 */
export async function collectContainerMemoryWarnings(): Promise<ContainerMemoryWarning[]> {
    const snapshots = await Promise.all(
        MONITOR_SERVICES.map(async (svc) => {
            const stats = await fetchContainerStats(svc.key);
            const percent = stats?.available
                ? containerMemoryPercent(stats.memoryUsed, stats.memoryLimit)
                : null;
            return { svc, percent };
        }),
    );
    return snapshots
        .filter((row) => row.percent != null && row.percent > CONTAINER_MEMORY_WARN_THRESHOLD)
        .map((row) => ({
            serviceName: row.svc.name,
            percent: row.percent as number,
        }));
}
